import { cn } from '@staff-app/ui';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';
import type { LiveDeviceStatus } from '../hooks/useDeviceHeartbeats';
import { DeviceStatusDot, classifyDeviceStatus, type MdmDeviceStatus } from './DeviceStatusDot';

interface Props {
  devices: MdmDevice[];
  onCardClick: (deviceId: string) => void;
  /** Live status by device UUID (open-api heartbeat); dot falls back to lastHeartBeat. */
  liveStatusByUuid?: Map<string, LiveDeviceStatus>;
}

// Screen-status badge styling per status (mirrors the dot colors).
const STATUS_META: Record<MdmDeviceStatus | LiveDeviceStatus, { label: string; cls: string }> = {
  online: { label: 'Online', cls: 'bg-success/10 text-success' },
  stale: { label: 'Stale', cls: 'bg-warning/10 text-warning' },
  hdmiOff: { label: 'HDMI off', cls: 'bg-warning/10 text-warning' },
  offline: { label: 'Offline', cls: 'bg-muted text-muted-foreground' },
};

/** "Playlist: H - Simple Playlist" / "Asset: images_1.jpeg" / "No content assigned". */
function assignLabel(device: MdmDevice): string {
  const type = device.currentType;
  const name = device.currentAssignName;
  if (!type || !name) return 'No content assigned';
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
  return `${typeLabel}: ${name}`;
}

export function DeviceGrid({ devices, onCardClick, liveStatusByUuid }: Props) {
  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      data-testid="mdm-devices-grid"
    >
      {devices.map((device) => {
        const live = device.UUID ? liveStatusByUuid?.get(device.UUID) : undefined;
        const status = live ?? classifyDeviceStatus(device);
        const meta = STATUS_META[status];
        return (
          <div
            key={device._id}
            data-testid="mdm-device-card"
            className={cn(
              'rounded-lg border bg-card p-3 shadow-sm transition-colors',
              'cursor-pointer hover:bg-accent/50',
            )}
            onClick={() => onCardClick(device._id)}
          >
            <div className="flex min-w-0 items-center gap-2">
              <DeviceStatusDot device={device} liveStatus={live} />
              <div className="truncate text-sm font-medium">{device.deviceName ?? '—'}</div>
            </div>

            {/* Screen status */}
            <span
              className={cn(
                'mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                meta.cls,
              )}
            >
              {meta.label}
            </span>

            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              {/* Assign type: content name */}
              <div className="truncate" title={assignLabel(device)}>
                {assignLabel(device)}
              </div>
              {/* Content id */}
              {device.currentAssignId ? (
                <div className="truncate font-mono" title={device.currentAssignId}>
                  {device.currentAssignId}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
